import React, { useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { User, LogOut, Menu, X, Zap, Crown, ShieldCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { NAV_ITEMS } from "./navConfig";
import Modal from "../common/Modal";

export default function Topbar({ onVip }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-white/8 bg-[#0a0a0b]/85 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <button className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-neutral-300 lg:hidden" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn">
              <Menu size={18} />
            </button>
            <div className="lg:hidden">
              <span className="grid h-8 w-8 place-items-center rounded-md bg-gradient-to-br from-[#f3f4f6] to-[#aeb3b9] text-black"><Zap size={15} /></span>
            </div>
            <div className="hidden sm:block">
              <div className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">Welcome,</div>
              <div className="font-head text-sm font-bold tracking-tight text-white">{user?.name || "Trader"}</div>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={onVip}
              className="btn-vip hidden items-center gap-2 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest transition-transform hover:-translate-y-0.5 sm:flex"
              data-testid="topbar-vip-btn"
            >
              <Crown size={13} /> Join Sanjay VIP
            </button>
            <button onClick={() => navigate("/profile")} className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-neutral-300 transition-colors hover:border-silver hover:text-white" data-testid="topbar-profile-btn" title="Profile">
              <User size={16} />
            </button>
            <button onClick={() => setLogoutOpen(true)} className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-neutral-400 transition-colors hover:border-rose-400/50 hover:text-rose-300" data-testid="topbar-logout-btn" title="Log out">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile full menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div className="fixed inset-0 z-[120] lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
            <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.35 }}
              className="absolute left-0 top-0 flex h-full w-72 flex-col border-r border-white/10 bg-[#0c0c0e] p-5">
              <div className="mb-8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="grid h-8 w-8 place-items-center rounded-md bg-gradient-to-br from-[#f3f4f6] to-[#aeb3b9] text-black"><Zap size={15} /></span>
                  <span className="font-head text-sm font-extrabold tracking-tight text-silver">SANJAY LIMITLESS</span>
                </div>
                <button onClick={() => setMenuOpen(false)} className="text-neutral-400"><X size={18} /></button>
              </div>
              <div className="mb-3 px-1 font-mono text-[10px] uppercase tracking-widest text-neutral-600">Workspace</div>
              <nav className="flex flex-col gap-1">
                {NAV_ITEMS.map((item) => (
                  <NavLink key={item.to} to={item.to} onClick={() => setMenuOpen(false)}
                    className={({ isActive }) => `relative flex items-center gap-3 rounded-xl px-3 py-3 font-mono text-[12px] uppercase tracking-wide transition-colors ${isActive ? "bg-white/[0.05] text-white" : "text-neutral-400 hover:bg-white/[0.02] hover:text-neutral-200"}`}>
                    {({ isActive }) => (<>{isActive && <span className="absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-full bg-silver" />}<item.icon size={17} /> {item.label}</>)}
                  </NavLink>
                ))}
                {user?.role === "admin" && (
                  <NavLink to="/admin" onClick={() => setMenuOpen(false)}
                    className={({ isActive }) => `relative flex items-center gap-3 rounded-xl px-3 py-3 font-mono text-[12px] uppercase tracking-wide transition-colors ${isActive ? "bg-white/[0.05] text-white" : "text-neutral-400 hover:bg-white/[0.02] hover:text-neutral-200"}`}>
                    <ShieldCheck size={17} /> Admin
                  </NavLink>
                )}
              </nav>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onVip();
                }}
                className="btn-vip mt-6 flex items-center justify-center gap-2 px-4 py-3 font-mono text-[11px] font-bold uppercase tracking-widest"
                data-testid="mobile-vip-btn"
              >
                <Crown size={13} /> Join Sanjay VIP
              </button>
              <p className="mt-auto pt-6 font-mono text-[9px] uppercase leading-relaxed tracking-widest text-neutral-700">Trading involves risk. Past performance does not guarantee future results.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Modal
        open={logoutOpen} onClose={() => setLogoutOpen(false)}
        title="Log out?" subtitle="You'll need to sign in again to access your workspace."
        testid="logout-modal"
        footer={
          <>
            <button onClick={() => setLogoutOpen(false)} className="btn-ghost rounded-xl px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest" data-testid="logout-cancel">Cancel</button>
            <button onClick={logout} className="btn-silver rounded-xl px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="logout-confirm">Log out</button>
          </>
        }
      />
    </>
  );
}
